import { useEffect, useState } from "react";
import { Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AutoAwesome as DraftIcon, Edit as RenameIcon } from "@mui/icons-material";
import type { Playbook } from "../../services/lensService";
import ConfirmDialog from "./ConfirmDialog";
import { plural } from "./lensUtils";

const MIN_PRECEDENT_LENGTH = 200;

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginBottom: theme.spacing(4) },
    titleRow: { display: "flex", alignItems: "center", gap: theme.spacing(2), flexWrap: "wrap" },
    spacer: { flexGrow: 1 },
    actions: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap" },
    panel: { marginTop: theme.spacing(4), display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    panelActions: { display: "flex", gap: theme.spacing(2) },
}));

interface PlaybookHeaderProps {
    playbook: Playbook;
    isBusy: boolean;
    // A generation is running, so another draft cannot start; everything else stays usable.
    generating: boolean;
    onRename: (displayName: string) => void;
    onToggleEnabled: () => void;
    onToggleArchived: () => void;
    onApproveAll: () => void;
    onDraftFromPrecedent: (text: string) => void;
}

const PlaybookHeader = ({
    playbook,
    isBusy: busy,
    generating,
    onRename,
    onToggleEnabled,
    onToggleArchived,
    onApproveAll,
    onDraftFromPrecedent,
}: PlaybookHeaderProps) => {
    const { classes } = useStyles();
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState(playbook.displayName);
    const [draftOpen, setDraftOpen] = useState(false);
    const [draftText, setDraftText] = useState("");
    const [confirming, setConfirming] = useState<"approve" | "archive" | null>(null);
    // Drafts with a review note are skipped by Approve all until the note is resolved.
    const drafts = playbook.provisions.filter((p) => p.position.status === "draft");
    const skipped = drafts.filter((p) => p.reviewNote).length;
    const approvable = drafts.length - skipped;

    useEffect(() => {
        setRenameOpen(false);
        setRenameValue(playbook.displayName);
    }, [playbook.key, playbook.displayName]);

    return (
        <Paper className={classes.root}>
            <Box className={classes.titleRow}>
                <Chip size="small" label={`v${playbook.version}`} />
                {!playbook.enabled && <Chip size="small" label="Disabled" />}
                {playbook.archived && <Chip size="small" label="Archived" />}
                <Typography variant="body2" style={{ color: "#777" }}>
                    {playbook.provisionCount} approved · {playbook.draftCount} draft
                </Typography>
                <span className={classes.spacer} />

                <Box className={classes.actions}>
                    {playbook.archived ? (
                        <Button variant="outlined" onClick={onToggleArchived} disabled={busy}>
                            Restore playbook
                        </Button>
                    ) : (
                        <>
                            <Button startIcon={<RenameIcon />} onClick={() => setRenameOpen((o) => !o)} disabled={busy}>
                                Rename
                            </Button>
                            <Button onClick={onToggleEnabled} disabled={busy}>
                                {playbook.enabled ? "Disable" : "Enable"}
                            </Button>
                            <Button onClick={() => setConfirming("archive")} disabled={busy}>
                                Archive
                            </Button>
                            {playbook.draftCount > 0 && (
                                <Button onClick={() => setConfirming("approve")} disabled={busy || approvable === 0}>
                                    Approve all drafts ({playbook.draftCount})
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<DraftIcon />}
                                onClick={() => setDraftOpen((o) => !o)}
                                disabled={busy || generating}
                            >
                                Draft from precedent
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {renameOpen && (
                <Box className={classes.panel}>
                    <TextField label="Display name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} fullWidth />
                    <Box className={classes.panelActions}>
                        <Button
                            variant="contained"
                            disabled={busy || !renameValue.trim()}
                            onClick={() => {
                                onRename(renameValue.trim());
                                setRenameOpen(false);
                            }}
                        >
                            Save name
                        </Button>
                        <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
                    </Box>
                </Box>
            )}

            {draftOpen && (
                <Box className={classes.panel}>
                    <Typography variant="body2" style={{ color: "#555" }}>
                        Paste the full text of a precedent document. Lens drafts an entry for each provision it finds that
                        this playbook does not already have. Every entry starts as a draft for review.
                    </Typography>
                    <TextField
                        multiline
                        minRows={8}
                        fullWidth
                        value={draftText}
                        onChange={(e) => setDraftText(e.target.value)}
                        placeholder="Paste precedent text here…"
                        helperText={
                            draftText.trim() && draftText.trim().length < MIN_PRECEDENT_LENGTH
                                ? `At least ${MIN_PRECEDENT_LENGTH} characters are needed.`
                                : undefined
                        }
                    />
                    <Box className={classes.panelActions}>
                        <Button
                            variant="contained"
                            disabled={busy || generating || draftText.trim().length < MIN_PRECEDENT_LENGTH}
                            onClick={() => {
                                onDraftFromPrecedent(draftText);
                                setDraftText("");
                                setDraftOpen(false);
                            }}
                        >
                            Draft provisions
                        </Button>
                        <Button onClick={() => setDraftOpen(false)}>Cancel</Button>
                    </Box>
                </Box>
            )}

            <ConfirmDialog
                open={confirming === "approve"}
                title={`Approve ${plural(approvable, "draft provision")}?`}
                confirmLabel="Approve all"
                onClose={() => setConfirming(null)}
                onConfirm={onApproveAll}
            >
                {skipped > 0 &&
                    `${plural(skipped, "draft")} with a review note ${skipped === 1 ? "is" : "are"} skipped until the note is resolved. `}
                Approved provisions are used in reviews straight away. Only bulk-approve drafts that have genuinely been
                reviewed.
            </ConfirmDialog>
            <ConfirmDialog
                open={confirming === "archive"}
                title={`Archive "${playbook.displayName}"?`}
                confirmLabel="Archive"
                onClose={() => setConfirming(null)}
                onConfirm={onToggleArchived}
            >
                It will no longer be offered for review. Nothing is deleted and it can be restored from the Archived list.
            </ConfirmDialog>
        </Paper>
    );
};

export default PlaybookHeader;
